<script>
import { mapState, mapGetters } from 'vuex';

import { formatAmount } from '../lib/formatters';
import { PaymentMethods } from '../';

export default {
  data() {
    return {
      scope: {
        scope: 'pay.js.marketing.payment_methods.agreement'
      }
    };
  },

  computed: {
    ...mapState([
      'productRecurrence',
      'selectedPspCode',
    ]),

    ...mapState({
      companyName: (state) => state.product.companyName,
      totalRecurrenceDiscountedAmount: (state) => {
        return state.amounts.recurrencePayment.discountedTotal.amountIncVat;
      },
      totalFirstRecurrenceDiscountedAmount: (state) => {
        return state.amounts.firstRecurrencePayment.discountedTotal.amountIncVat;
      }
    }),

    ...mapGetters([
      'getPspByCode',
      'hasDifferentPriceForFirstPeriod',
      'isFreeForFirstPeriod'
    ]),

    formattedTotalRecurrenceDiscountedAmount() {
      return formatAmount(this.totalRecurrenceDiscountedAmount);
    },

    formattedTotalFirstRecurrenceDiscountedAmount() {
      return formatAmount(this.totalFirstRecurrenceDiscountedAmount);
    },

    isCreditCard() {
      return this.selectedPspCode === PaymentMethods.CREDIT_CARD;
    },

    isDirectDebit() {
      return this.selectedPspCode === PaymentMethods.SEPA_DIRECT_DEBIT;
    },

    firstPeriodMessage() {
      const { periodsType, periodsMultiplier, period1 } = this.productRecurrence;
      const periodTerm = this.t(
        `pay.js.marketing.summary.recurrence_message.first_period.${periodsType}`,
        { count: periodsMultiplier }
      );

      return `${periodTerm} ${period1}`;
    }
  }
};
</script>

<template>
  <span>
    <template v-if="productRecurrence.isOnDemand && !isDirectDebit">
      {{ t('on_demand', { ...scope, company: companyName }) }}
    </template>

    <template v-else-if="productRecurrence.isSubscription">
      <template v-if="isFreeForFirstPeriod">
        {{ t('subscription.first_period_free', {
          ...scope,
          amount: formattedTotalRecurrenceDiscountedAmount,
          period: productRecurrence.period
        }) }}
      </template>

      <template v-else-if="isDirectDebit">
        <template v-if="hasDifferentPriceForFirstPeriod">
          {{ t('subscription.direct_debit_with_different_first_period', {
            ...scope,
            amount: formattedTotalRecurrenceDiscountedAmount,
            first_period: firstPeriodMessage,
            first_period_amount: formattedTotalFirstRecurrenceDiscountedAmount,
            period: productRecurrence.period
          }) }}
        </template>

        <template v-else>
          {{ t('subscription.direct_debit', {
            ...scope,
            amount: formattedTotalRecurrenceDiscountedAmount,
            period: productRecurrence.period
          }) }}
        </template>
      </template>

      <template v-else-if="isCreditCard">
        {{ t('subscription.credit_card', {
          ...scope,
          amount: formattedTotalRecurrenceDiscountedAmount,
          period: productRecurrence.period
        }) }}
      </template>

      <template v-else>
        {{ t('subscription.not_direct_debit', {
          ...scope,
          amount: formattedTotalRecurrenceDiscountedAmount,
          period: productRecurrence.period,
          psp: getPspByCode(selectedPspCode).fullName
        }) }}
      </template>
    </template>

    <template v-else>
      <template v-if="isFreeForFirstPeriod">
        {{ t('installments.first_period_free', {
          ...scope,
          periods_count: productRecurrence.quantity - 1,
          installments: t('installments.installments', {
            ...scope,
            count: productRecurrence.quantity - 1
          }),
          period: productRecurrence.period,
          amount: formattedTotalRecurrenceDiscountedAmount
        }) }}
      </template>

      <template v-else-if="isDirectDebit">
        <template v-if="hasDifferentPriceForFirstPeriod">
          {{ t('installments.direct_debit_with_different_first_period', {
            ...scope,
            first_period_amount: formattedTotalFirstRecurrenceDiscountedAmount,
            periods_count: productRecurrence.quantity - 1,
            installments: t('installments.installments', {
              ...scope,
              count: productRecurrence.quantity - 1
            }),
            amount: formattedTotalRecurrenceDiscountedAmount,
            period: productRecurrence.period
          }) }}
        </template>

        <template v-else>
          {{ t('installments.direct_debit', {
            ...scope,
            periods_count: productRecurrence.quantity,
            installments: t('installments.installments', {
              ...scope,
              count: productRecurrence.quantity
            }),
            amount: formattedTotalRecurrenceDiscountedAmount,
            period: productRecurrence.period
          }) }}
        </template>
      </template>

      <template v-else-if="isCreditCard">
        {{ t('installments.credit_card', {
          ...scope,
          periods_count: productRecurrence.quantity - 1,
          installments: t('installments.installments', { ...scope, count: productRecurrence.quantity - 1 }),
          amount: formattedTotalRecurrenceDiscountedAmount,
          period: productRecurrence.period
        }) }}
      </template>

      <template v-else>
        {{ t('installments.not_direct_debit', {
          ...scope,
          periods_count: productRecurrence.quantity - 1,
          installments: t('installments.installments', {
            ...scope,
            count: productRecurrence.quantity - 1
          }),
          amount: formattedTotalRecurrenceDiscountedAmount,
          period: productRecurrence.period
        }) }}
      </template>
    </template>
  </span>
</template>
